import React, { useState } from 'react';
import {
  Sparkles,
  ArrowRight,
  Check,
  X,
  AlertTriangle,
  CalendarClock,
  Lightbulb,
  Loader2,
  ChevronDown,
  ChevronUp,
  Tag,
  MapPin,
  RefreshCw,
} from 'lucide-react';

const CATEGORY_MAP = {
  financieel: 'Financieel & Belasting',
  contracten: 'Contracten & Huur',
  leveranciers: 'Leveranciers & Inkoop',
  techniek: 'Techniek & Apparatuur',
  voertuigen: 'Voertuigen',
  vastgoed: 'Vastgoed & Facilitair',
  veiligheid: 'Veiligheid & Compliance',
  milieu: 'Milieu & Infrastructuur',
  nieuwbouw: 'Nieuwbouw & Verbouwing',
  overnames: 'Overnames',
  automatisering: 'Automatisering',
};

const URGENCY_STYLES = {
  high: 'bg-red-50 border-red-200 text-red-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  low: 'bg-green-50 border-green-200 text-green-700',
};

const URGENCY_LABELS = {
  high: 'Urgent',
  medium: 'Binnenkort',
  low: 'Toekomstig',
};

const RECURRENCE_LABELS = {
  yearly: 'Jaarlijks',
  quarterly: 'Driemaandelijks',
  monthly: 'Maandelijks',
  'one-time': 'Eenmalig',
};

export default function AINormalizationPanel({
  result,
  loading,
  error,
  originalTitle,
  originalContent,
  onApplyAll,
  onApplyField,
  onAcceptCompliance,
  onDismiss,
  onRetry,
  compact = false,
}) {
  const [expanded, setExpanded] = useState(true);
  const [acceptedCompliance, setAcceptedCompliance] = useState({});

  if (loading) {
    return (
      <div data-testid="ai-normalize-loading" className="rounded-xl border border-[#2E75B6]/20 bg-[#2E75B6]/[0.03] p-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
            <Loader2 className="w-4 h-4 text-[#2E75B6] animate-spin" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700">AI Normalisatie</p>
            <p className="text-xs text-slate-400">Tekst wordt geanalyseerd...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !result) {
    return (
      <div data-testid="ai-normalize-error" className="rounded-xl border border-red-200 bg-red-50/50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-700">Normalisatie mislukt</p>
              <p className="text-xs text-red-500">{error}</p>
            </div>
          </div>
          {onRetry && (
            <button
              data-testid="ai-normalize-retry"
              onClick={onRetry}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 hover:bg-red-100 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Opnieuw
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!result) return null;

  const {
    cleaned_title,
    cleaned_content,
    category,
    category_confidence,
    location,
    tags,
    compliance_items,
    suggestions,
    summary,
  } = result;

  const hasChanges = cleaned_title !== originalTitle || category || location || (tags && tags.length > 0);
  const hasCompliance = compliance_items && compliance_items.length > 0;
  const hasSuggestions = suggestions && suggestions.length > 0;

  return (
    <div data-testid="ai-normalize-panel" className="rounded-xl border border-[#2E75B6]/20 bg-[#2E75B6]/[0.02] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#2E75B6]/[0.05] border-b border-[#2E75B6]/10">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-[#2E75B6]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">AI Normalisatie</p>
            {summary && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{summary}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {onApplyAll && hasChanges && (
            <button
              data-testid="ai-normalize-apply-all"
              onClick={() => onApplyAll(result)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors"
            >
              <Check className="w-3 h-3" />
              Alles overnemen
            </button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {onDismiss && (
            <button
              data-testid="ai-normalize-dismiss"
              onClick={onDismiss}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Title comparison */}
          {cleaned_title && cleaned_title !== originalTitle && (
            <div data-testid="ai-normalize-title-diff">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">Titel</p>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-400 line-through truncate mb-1">{originalTitle}</div>
                  <div className="text-sm text-slate-800 font-medium">{cleaned_title}</div>
                </div>
                {onApplyField && (
                  <button
                    data-testid="ai-apply-title"
                    onClick={() => onApplyField('title', cleaned_title)}
                    className="flex-shrink-0 p-1.5 rounded-lg text-[#2E75B6] hover:bg-[#2E75B6]/10 transition-colors"
                    title="Titel overnemen"
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Content comparison */}
          {cleaned_content && cleaned_content !== originalContent && !compact && (
            <div data-testid="ai-normalize-content-diff">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">Opgeschoonde inhoud</p>
              <div className="bg-white rounded-lg border border-slate-200 p-3 text-xs text-slate-700 max-h-32 overflow-y-auto leading-relaxed whitespace-pre-wrap">
                {cleaned_content}
              </div>
              {onApplyField && (
                <button
                  data-testid="ai-apply-content"
                  onClick={() => onApplyField('content', cleaned_content)}
                  className="mt-1.5 flex items-center gap-1 text-xs text-[#2E75B6] hover:text-[#27649C] transition-colors"
                >
                  <ArrowRight className="w-3 h-3" />
                  Inhoud overnemen
                </button>
              )}
            </div>
          )}

          {/* Category, Location, Tags row */}
          <div className="flex flex-wrap gap-1.5">
            {category && (
              <button
                data-testid="ai-normalize-category"
                onClick={() => onApplyField && onApplyField('category', category)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
                  category_confidence === 'high'
                    ? 'border-[#2E75B6] bg-[#2E75B6]/5 text-[#2E75B6]'
                    : category_confidence === 'medium'
                    ? 'border-[#2E75B6]/50 bg-[#2E75B6]/5 text-[#2E75B6]/80'
                    : 'border-slate-300 bg-slate-50 text-slate-600'
                }`}
              >
                <Tag className="w-3 h-3" />
                {CATEGORY_MAP[category] || category}
              </button>
            )}

            {location && (
              <button
                data-testid="ai-normalize-location"
                onClick={() => onApplyField && onApplyField('location', location)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-[#2E75B6]/30 bg-[#2E75B6]/5 text-xs font-medium text-[#2E75B6]/80 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
              >
                <MapPin className="w-3 h-3" />
                {location}
              </button>
            )}

            {tags && tags.map((tag) => (
              <button
                data-testid={`ai-normalize-tag-${tag}`}
                key={tag}
                onClick={() => onApplyField && onApplyField('tag', tag)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 bg-white text-[11px] text-slate-600 transition-all hover:scale-[1.02] hover:border-[#2E75B6]/30 active:scale-[0.98] cursor-pointer"
              >
                #{tag}
              </button>
            ))}
          </div>

          {/* Compliance Items */}
          {hasCompliance && (
            <div data-testid="ai-normalize-compliance">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2 flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5" />
                Gedetecteerde compliance-items ({compliance_items.length})
              </p>
              <div className="space-y-2">
                {compliance_items.map((item, idx) => (
                  <div
                    key={idx}
                    data-testid={`ai-compliance-item-${idx}`}
                    className={`rounded-lg border p-3 ${URGENCY_STYLES[item.urgency] || URGENCY_STYLES.medium}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold">{item.title}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${URGENCY_STYLES[item.urgency] || ''}`}>
                            {URGENCY_LABELS[item.urgency] || item.urgency}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] opacity-80">
                          <span>Type: {item.type}</span>
                          {item.due_date && <span>Deadline: {item.due_date}</span>}
                          <span>{RECURRENCE_LABELS[item.recurrence] || item.recurrence}</span>
                        </div>
                      </div>
                      {onAcceptCompliance && !acceptedCompliance[idx] && (
                        <button
                          data-testid={`ai-accept-compliance-${idx}`}
                          onClick={() => {
                            setAcceptedCompliance(prev => ({ ...prev, [idx]: true }));
                            onAcceptCompliance(item);
                          }}
                          className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/80 border border-current/20 text-xs font-medium hover:bg-white transition-colors"
                        >
                          <Check className="w-3 h-3" />
                          Aanmaken
                        </button>
                      )}
                      {acceptedCompliance[idx] && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <Check className="w-3 h-3" />
                          Aangemaakt
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions */}
          {hasSuggestions && (
            <div data-testid="ai-normalize-suggestions">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2 flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5" />
                Suggesties
              </p>
              <ul className="space-y-1.5">
                {suggestions.map((suggestion, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed"
                  >
                    <span className="w-4 h-4 rounded-full bg-[#2E75B6]/10 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-[10px] text-[#2E75B6] font-medium">{idx + 1}</span>
                    </span>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
